import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { VectorApiCall } from '@modules/Vector/F/Function/sizeCalculator/api/api.call';
import { CUTLINER_OPTS, MIN_SIDE_WITH_MARGIN_MM } from '@modules/Vector/S/Constant/stickerizer.constant';
import { SvgUtil } from '@modules/Vector/S/Function/util.svg';
import { UtilTimer } from '@modules/Vector/S/Function/util.timer';
import { UtilLogger } from '@modules/Vector/S/Function/util.logger';
import {
  createJogakStickerGangCalculator,
  GANGING_SPEC,
  ItemSizeCalculator,
} from '../sizeCalculator/sizeCalculator';
import {
  ApiNetworkError,
  ApiRequestError,
  ApiTimeoutError,
  InstanceNotInitializedError,
  InvalidConstructorCallError,
  InvalidInputError,
  NoCutlineSvgWithImageError,
  NotFoundInCacheError,
  StickerizerError,
} from './stickerizer.error';
import { StickerizerHelper } from './stickerizer.helper';
import type {
  CutlineProcessResult,
  PreProcessInfo,
  PrintablesParams,
  PrintablesResult,
  ProcessCacheKey,
  ProcessParams,
  ProcessResult,
  Size,
  StickerizerInput,
} from './stickerizer.types';
import { StickerizerValidator } from './stickerizer.validator';
import { MinMaxScaleInvalidError } from '@modules/Vector/F/Function/sizeCalculator/sizeCalculator.error.ts';
import { SvgMaterialComposer } from '@modules/Vector/S/Function/composite/svg.material-composer.ts';
import {
  JogakStickerGangingItem,
  PlushGangSpec,
  TtibuGangSpec,
} from '@modules/Df/TechPack/S/types/techPack.ts';

// Re-export types for external use
export type {
  CutlineProcessResult,
  PreProcessInfo,
  Printable,
  PrintablesResult,
  ProcessCacheKey,
  ProcessParams,
  ProcessResult,
  Size,
  StickerizerInput,
} from './stickerizer.types';

export class Stickerizer {
  private static _allowConstructor = false;
  private readonly imageUrl: string;
  private readonly debug: boolean;
  private preprocessInfo: PreProcessInfo | null = null;
  private processResultMap: Map<ProcessCacheKey, CutlineProcessResult> = new Map();
  private lastProcessCacheKey: ProcessCacheKey | null = null;
  private vectorApi: VectorApiCall;
  private helper: StickerizerHelper;
  private validator: StickerizerValidator;
  private sizeCalulator: ItemSizeCalculator | null = null;
  private readonly sizeSpecs: Record<string, JogakStickerGangingItem | PlushGangSpec | TtibuGangSpec>;
  private readonly minSizeWidthMargin: number;
  private readonly with_s3_cache: boolean;

  private constructor(params: StickerizerInput) {
    if (!Stickerizer._allowConstructor) {
      throw new InvalidConstructorCallError();
    }
    this.imageUrl = params.image_url;
    this.debug = params.debug ?? false;
    this.vectorApi = new VectorApiCall();
    this.helper = new StickerizerHelper(this.debug);
    this.validator = new StickerizerValidator();

    if (!params.bp_id) throw new Error('bp_id가 없습니다');

    const sizeSpecs = GANGING_SPEC?.[params.bp_id];

    if (!sizeSpecs) throw new Error(`No gang spec`);

    this.sizeSpecs = sizeSpecs;
    this.minSizeWidthMargin = MIN_SIDE_WITH_MARGIN_MM[params.bp_id] ?? 30;
    this.with_s3_cache = params.with_s3_cache ?? true;
  }

  /**
   * Factory method to create and initialize a Stickerizer instance.
   * @param params StickerizerInput
   */
  public static async create(params: StickerizerInput): Promise<Stickerizer> {
    Stickerizer._allowConstructor = true;
    const instance = new Stickerizer(params);
    Stickerizer._allowConstructor = false;

    // Validate image size before initialization
    await instance.validator.validateImageSize(params.image_url, CUTLINER_OPTS.IMG_FILE_LIMIT_SIZE_MB);

    // Initialize preprocessing
    await instance.initialize();
    return instance;
  }

  /**
   * Initialize preprocessing: fetch and cache preprocessed image info if not already cached.
   * Should be called after instantiation.
   */
  private async initialize(): Promise<void> {
    this.preprocessInfo = await this.prepare(this.imageUrl);

    this.sizeCalulator = createJogakStickerGangCalculator(this.sizeSpecs, this.minSizeWidthMargin);
  }

  /**
   * Always creates the sticker info (preprocessed image, trimmed image, and its outline SVG)
   * @param imageUrl - The original image URL to preprocess
   * @returns The sticker info (preprocessed image, trimmed image, and its outline SVG)
   */
  private async prepare(imageUrl: string): Promise<PreProcessInfo> {
    try {
      // 1. 이미지 투명 여백 Trim 적용하여 이미지 크기 정리 (최대 사이즈 제한 리사이즈 포함)
      UtilTimer.start('prepare:trim');
      const trimmed_image = await this.vectorApi.trimImageWithSizeLimit(
        {
          image_url: imageUrl,
          size_limit: CUTLINER_OPTS.IMG_SIZE_LIMIT,
          trim_margin: 0, // 0 으로 고정
          alpha_threshold: CUTLINER_OPTS.DEFAULT_ALPHA_THRESHOLD,
          with_s3_cache: this.with_s3_cache,
        },
        true,
      );
      UtilTimer.end('prepare:trim', 'yellow');
      if (trimmed_image.is_s3_cache_hit) {
        UtilLogger.info('💾 S3 Cache hit for trimImageWithSizeLimit');
      }
      this.validator.validateImageResponse(trimmed_image, 'trimImage');

      // 2. 이미지 픽셀을 이진화 처리한 이미지 생성
      UtilTimer.start('prepare:binarize');
      const binarized_image = await this.vectorApi.binarizeImage(
        {
          image_url: trimmed_image.url,
          resize: CUTLINER_OPTS.DEFAULT_IMG_RESIZE,
          resize_margin: CUTLINER_OPTS.DEFAULT_IMG_RESIZE_MARGIN,
          alpha_threshold: CUTLINER_OPTS.DEFAULT_ALPHA_THRESHOLD,
          with_s3_cache: this.with_s3_cache,
        },
        true,
      );
      UtilTimer.end('prepare:binarize', 'orange');
      if (binarized_image.is_s3_cache_hit) {
        UtilLogger.info('💾 S3 Cache hit for binarizeImage');
      }
      this.validator.validateImageResponse(binarized_image, 'binarizeImage');

      // 3.  Outline (윤곽선) SVG 생성
      UtilTimer.start('prepare:outline');
      const outline = await this.vectorApi.vectorizeOutline(
        { image_url: binarized_image.url, with_s3_cache: this.with_s3_cache },
        true,
      );
      UtilTimer.end('prepare:outline', 'green');
      if (outline.is_s3_cache_hit) {
        UtilLogger.info('💾 S3 Cache hit for vectorizeOutline');
      }
      this.validator.validateOutlineResponse(outline, 'outline vectorize');
      // Download files for debugging
      await this.helper.downloadFileFromUrl(trimmed_image.url, 'trimmed_image.png');
      await this.helper.downloadFileFromUrl(binarized_image.url, 'binarized_image.png');
      this.helper.downloadSvgFile(outline.svg, 'outline.svg');

      return {
        binarized_image,
        trimmed_image,
        outline,
      };
    } catch (err: any) {
      if (
        err instanceof ApiRequestError ||
        err instanceof ApiTimeoutError ||
        err instanceof ApiNetworkError
      ) {
        throw new StickerizerError(
          `VectorApi error in createStickerInfo: ${err.message}`,
          err.code || 'VECTOR_API_ERROR',
          { original: err, imageUrl },
        );
      }
      throw err;
    }
  }

  /**
   * Process the image for stickerization.
   * @param params - Process parameters including target size, offset distance, and stroke style
   * @returns Process result with thumbnail SVG and dimension information
   */
  public async process(params: ProcessParams): Promise<ProcessResult> {
    const {
      targetSizeMm,
      offsetDistanceMm = CUTLINER_OPTS.DEFAULT_OFFSET_MM,
      strokeStyle,
      smoothOffsetAdder = CUTLINER_OPTS.SMOOTH_OFFSET_ADDER,
      materialSource,
      afterprocessFn,
      thumbnailExpandMargin = CUTLINER_OPTS.DEFAULT_THUMBNAIL_EXPAND_MARGIN,
    } = params;

    if (!this.preprocessInfo) throw new NotFoundInCacheError();

    // 사이즈 범위 다시 한번 더 체크
    if (this.sizeCalulator) {
      const { width, height } = this.preprocessInfo.trimmed_image;
      const aspectRatio = width / height;
      this.sizeCalulator.getMinMaxItemSize(aspectRatio, offsetDistanceMm);
    }

    const cacheKey = StickerizerHelper.generateProcessCacheKey(
      targetSizeMm,
      offsetDistanceMm,
      smoothOffsetAdder,
    );
    const memory_cached = this.processResultMap.get(cacheKey);

    // 동일한 조건으로 이미 처리된 결과가 있으면 캐시에서 바로 반환
    if (memory_cached) {
      this.lastProcessCacheKey = cacheKey;
      UtilLogger.info('💾 Memory cache hit for process');

      const expanded_svg = SvgUtil.expandViewBoxMargin(
        memory_cached.cutline_svg_with_image,
        thumbnailExpandMargin,
      );

      const materializedSvg = !UtilS.isEmpty(materialSource)
        ? await SvgMaterialComposer.composeStickerWithMaterial(expanded_svg, materialSource)
        : expanded_svg;

      const finalThumbnailSvg = afterprocessFn ? await afterprocessFn(materializedSvg) : materializedSvg;

      return {
        thumbnail_svg: finalThumbnailSvg,
        dimension: memory_cached.dimension,
      };
    }

    // 300 DPI 기준으로 Target Size 계산
    const offsetDistancePx = StickerizerHelper.mmTo300DpiPx(offsetDistanceMm);

    // Step 1: Calculate the effective target area (excluding offset)
    const targetSizePxWithoutMargin = StickerizerHelper.mmTo300DpiPxSize({
      width: targetSizeMm.width - 2 * offsetDistanceMm,
      height: targetSizeMm.height - 2 * offsetDistanceMm,
    });

    // Step 2: Calculate scale factor between effective target area and preprocessed outline
    // Both should be in same units (px) for accurate comparison
    const outlineSizePx = {
      width: this.preprocessInfo.outline.width,
      height: this.preprocessInfo.outline.height,
    };

    // Calculate scale factor for fit inside the target size
    const scaleFactor = StickerizerHelper.calculateFitInsideScaleFactor(
      targetSizePxWithoutMargin,
      outlineSizePx,
    );

    const { outline } = this.preprocessInfo;

    let pre_cutline_svg: string;
    try {
      // Step 4: Apply offset to outline svg with scale factor consideration
      // The offset distance needs to be divided by scale factor because
      // the final cutline will be scaled up by scaleFactor
      const adjustedOffsetDistance = offsetDistancePx / scaleFactor;

      UtilTimer.start('process:generateCutline');
      const result = await this.vectorApi.generateCutline(
        {
          svg: UtilS.escape(outline.svg, ''),
          offset_distance: adjustedOffsetDistance, // 2 mm
          stroke_style: strokeStyle,
          smooth_offset_adder: smoothOffsetAdder,
          with_s3_cache: this.with_s3_cache,
          cache_key: cacheKey,
          image_url: this.imageUrl,
        },
        true,
      );

      UtilTimer.end('process:generateCutline', 'yellow');
      if (result.is_s3_cache_hit) {
        UtilLogger.info('💾 S3 Cache hit for generateCutline');
      }
      pre_cutline_svg = result.svg;
    } catch (err: any) {
      if (
        err instanceof ApiRequestError ||
        err instanceof ApiTimeoutError ||
        err instanceof ApiNetworkError
      ) {
        throw new StickerizerError(
          `VectorApi error in process (generateCutline): ${err.message}`,
          err.code || 'VECTOR_API_ERROR',
          { original: err, imageUrl: this.imageUrl },
        );
      }
      throw err;
    }

    const pre_cutline_svg_size_in_px = SvgUtil.extractSvgSize(pre_cutline_svg);

    const scaled_pre_cutline_size = StickerizerHelper.pxTo300DpiMmSize({
      width: pre_cutline_svg_size_in_px.width * scaleFactor,
      height: pre_cutline_svg_size_in_px.height * scaleFactor,
    });

    // Step 5: Calculate adjustedScaleFactor to ensure the final cutline fits within targetSizeMm
    // scaled_pre_cutline_size가 targetSizeMm에 fit inside되도록 조정된 scaleFactor 계산
    const adjustedScaleFactor = StickerizerHelper.calculateFitInsideScaleFactor(
      targetSizeMm,
      scaled_pre_cutline_size,
    );

    // 최종 scaleFactor는 기존 scaleFactor와 adjustedScaleFactor를 곱한 값
    const finalScaleFactor = scaleFactor * adjustedScaleFactor;

    // Step 6: Scale up the pre_cutline_svg to achieve the target size with adjusted scale factor
    UtilTimer.start('process:scaleSvg');
    const cutline_svg = await SvgUtil.scaleSvgByRecalculating(pre_cutline_svg, finalScaleFactor);
    UtilTimer.end('process:scaleSvg', 'orange');

    // 캐시 크기 최적화를 위해 embed 대신 URL 링크 방식 사용
    const cutline_svg_with_image = await SvgUtil.putImageToSvgWithLink(
      cutline_svg,
      this.preprocessInfo.trimmed_image.url,
      offsetDistancePx,
    );

    const cutline_svg_dimension = SvgUtil.getSvgDimension(cutline_svg, 300);

    const result: CutlineProcessResult = {
      cutline_svg,
      cutline_svg_with_image,
      dimension: cutline_svg_dimension,
    };

    this.processResultMap.set(cacheKey, result);
    this.lastProcessCacheKey = cacheKey;

    // Download files for debugging
    this.helper.downloadSvgFile(cutline_svg, 'cutline.svg');
    this.helper.downloadSvgFile(cutline_svg_with_image, 'thumbnail.svg');

    // thumbnail 이미지는 stroke 선이 rendering 에서 잘리지 않게 하기 위해 viewBox 에 margin 확장 처리
    const expanded_svg = SvgUtil.expandViewBoxMargin(cutline_svg_with_image, thumbnailExpandMargin);

    // materialSource가 있는 경우 composeStickerWithMaterial 적용
    const materializedSvg = !UtilS.isEmpty(materialSource)
      ? await SvgMaterialComposer.composeStickerWithMaterial(expanded_svg, materialSource)
      : expanded_svg;

    UtilTimer.start('process:finalSvg');
    const finalThumbnailSvg = afterprocessFn ? await afterprocessFn(materializedSvg) : materializedSvg;
    UtilTimer.end('process:finalSvg', 'cyan');

    return {
      thumbnail_svg: finalThumbnailSvg,
      dimension: result.dimension,
    };
  }

  /**
   * Create thumbnail canvas with optional material composition
   * @param expandedSvg - Expanded SVG with image embedded
   * @param materialSource - Optional material source for composition
   * @returns Promise<HTMLCanvasElement> - Rasterized thumbnail canvas
   */
  private async createThumbnailCanvas(
    expandedSvg: string,
    params?: PrintablesParams,
  ): Promise<HTMLCanvasElement> {
    try {
      const baseSvg = params?.materialSource
        ? await SvgMaterialComposer.composeStickerWithMaterial(expandedSvg, params.materialSource)
        : expandedSvg;

      const processedSvg = params?.afterprocessFn ? await params.afterprocessFn(baseSvg) : baseSvg;

      // const embeddedSvg = await SvgUtil.convertImageLinkToEmbedInSvg(processedSvg);

      return SvgUtil.toRasterize(processedSvg);
    } catch (error) {
      UtilLogger.error('Failed to create thumbnail with material, falling back to original');
      // Fallback to original SVG without material
      return await SvgUtil.toRasterize(expandedSvg);
    }
  }

  /**
   * Convert the sticker info to printables. (300dpi)
   * @param params - Printables parameters including material source
   * @returns Printables result with thumbnail_canvas, design_canvas, cutline_svg and dimension
   */
  public async toPrintables(params?: PrintablesParams): Promise<PrintablesResult> {
    if (!this.lastProcessCacheKey) throw new NoCutlineSvgWithImageError();

    const processResult = this.getProcessResult();
    if (!processResult || !processResult.cutline_svg_with_image) throw new NoCutlineSvgWithImageError();

    // uploadProcessResultToS3 method 호출
    // - cutline_svg, cutline_svg_with_image 를 dimension (metadata) 정보와 함께 업로드 한다.
    // - 업로드 파일명 규칙은 this.imageUrl 을 FileUtil.extractFilenameFromUrl 를 사용해 baseFilename 을 추출하고 SUFFIX 로 cacheKey 를 추가
    // - VectorApiCall.

    const normalized_cutline_svg_with_image = await SvgUtil.normalizeViewBoxWithScale(
      processResult.cutline_svg_with_image,
      3, // precision for svg coordinate calculation
    );

    UtilTimer.start('toPrintables:normalizeViewBoxWithScale');
    const normalized_cutline_svg_with_image_embed = await SvgUtil.convertImageLinkToEmbedInSvg(
      normalized_cutline_svg_with_image,
    );
    UtilTimer.end('toPrintables:normalizeViewBoxWithScale', 'orange');

    // Create thumbnail_canvas with expanded viewBox margin
    const expanded_normalized_cutline_svg_with_image_embed = SvgUtil.expandViewBoxMargin(
      normalized_cutline_svg_with_image_embed,
      params?.thumbnailExpandMargin ?? CUTLINER_OPTS.DEFAULT_THUMBNAIL_EXPAND_MARGIN,
    );

    UtilTimer.start('toPrintables:toRasterize');

    // Create thumbnail canvas with optional material composition
    const thumbnailCanvas = await this.createThumbnailCanvas(
      expanded_normalized_cutline_svg_with_image_embed,
      params,
    );

    // Split SVG into design (with image) and cutline (without image)
    const { svgHavingImage: printableDesignSvg, svgExcludeImage: printableCutlineSvg } =
      await SvgUtil.splitImageFromSvg(normalized_cutline_svg_with_image_embed);

    const designCanvas = await SvgUtil.toRasterize(printableDesignSvg);
    UtilTimer.end('toPrintables:toRasterize', 'green');

    const expandMarginMm = params?.expandMargin?.() ?? 0;

    let expandedCutlineSvg: string | null = null;
    if (expandMarginMm > 0) {
      try {
        const res = await this.vectorApi.applyOffsetFromSvg({
          svgString: UtilS.escape(printableCutlineSvg, ''),
          offset: expandMarginMm,
          isMm: true,
        });
        if (res?.success && res.svg) {
          expandedCutlineSvg = res.svg;
        } else {
          console.warn('[applyOffsetFromSvg] failed, fallback to original cutline', res);
        }
      } catch (err) {
        console.warn('[applyOffsetFromSvg] error, fallback to original cutline', err);
      }
    }

    const finalCutlineSvg = expandedCutlineSvg ?? printableCutlineSvg;

    const printablesResult: PrintablesResult = {
      thumbnail_canvas: thumbnailCanvas,
      design_canvas: designCanvas,
      cutline_svg: finalCutlineSvg,
      dimension: SvgUtil.getSvgDimension(normalized_cutline_svg_with_image_embed, 300),
    };

    // Download files for debugging
    this.helper.downloadSvgFile(expanded_normalized_cutline_svg_with_image_embed, 'printable_thumbnail.svg');
    this.helper.downloadSvgFile(printableDesignSvg, 'printable_design.svg');
    this.helper.downloadSvgFile(finalCutlineSvg, 'printable_cutline.svg');

    return printablesResult;
  }

  /**
   * Get min and max scale values for the item using size calculator
   * @param offsetMm - Offset distance in mm (used as margin)
   * @returns Object containing min and max scale values and getSizeAtScale function
   * @returns.minScale - Minimum scale value
   * @returns.maxScale - Maximum scale value
   * @returns.getSizeAtScale - Function to get item size at specific scale with optional margin
   * @returns.getSizeAtNormalizedScale - Function to get item size using normalized scale (0-1) with optional margin
   */
  public getMinMaxScale(offsetMm: number): {
    minScale: number;
    maxScale: number;
    getSizeAtScale: (scale: number, withMargin?: boolean, precision?: number) => Size;
    getSizeAtNormalizedScale: (normalizedScale: number, withMargin?: boolean, precision?: number) => Size;
  } {
    if (!this.preprocessInfo || !this.sizeCalulator) {
      throw new InstanceNotInitializedError();
    }

    const { width, height } = this.preprocessInfo.trimmed_image;
    const aspectRatio = width / height;

    const sizeCalculationResult = this.sizeCalulator.getMinMaxItemSize(aspectRatio, offsetMm);

    const getSizeAtScale = (scale: number, withMargin = true, precision = 1): Size => {
      const itemSize = this.sizeCalulator!.getCurrentSize(scale);

      if (withMargin) {
        return {
          width: StickerizerHelper.roundToPrecision(itemSize.width + 2 * offsetMm, precision),
          height: StickerizerHelper.roundToPrecision(itemSize.height + 2 * offsetMm, precision),
        };
      }

      return itemSize;
    };

    const getSizeAtNormalizedScale = (normalizedScale: number, withMargin = true, precision = 1): Size => {
      if (normalizedScale < 0 || normalizedScale > 1) {
        throw new InvalidInputError('normalizedScale must be between 0 and 1');
      }

      // Convert normalized scale (0-1) to actual scale using min and max scale
      const slope = (sizeCalculationResult.item.max.scale - sizeCalculationResult.item.min.scale) / (1 - 0);
      const actualScale = sizeCalculationResult.item.min.scale + normalizedScale * slope;

      return getSizeAtScale(actualScale, withMargin, precision);
    };

    if (sizeCalculationResult.item.min.scale >= sizeCalculationResult.item.max.scale) {
      // throw new StickerizerError('minScale is greater or equal to maxScale', 'MIN_MAX_SCALE_ERROR');
      throw new MinMaxScaleInvalidError(
        sizeCalculationResult.item.min.scale,
        sizeCalculationResult.item.max.scale,
      );
    }

    return {
      minScale: sizeCalculationResult.item.min.scale,
      maxScale: sizeCalculationResult.item.max.scale,
      getSizeAtScale,
      getSizeAtNormalizedScale,
    };
  }

  /**
   * Check if a process result is cached for the given target size, offset distance, and smooth offset adder.
   * @param targetSizeMm - Target size in mm
   * @param offsetDistanceMm - Offset distance in mm
   * @param smoothOffsetAdder - Smooth offset adder value (optional)
   * @returns {boolean} True if the process result is cached, false otherwise
   */
  public isProcessResultCached(
    targetSizeMm: Size,
    offsetDistanceMm: number,
    smoothOffsetAdder = CUTLINER_OPTS.SMOOTH_OFFSET_ADDER,
  ): boolean {
    const cacheKey = StickerizerHelper.generateProcessCacheKey(
      targetSizeMm,
      offsetDistanceMm,
      smoothOffsetAdder,
    );
    return this.processResultMap.has(cacheKey);
  }

  public destroy(): void {
    this.processResultMap.clear();
    if (this.preprocessInfo) {
      this.preprocessInfo = null;
    }
    this.lastProcessCacheKey = null;
  }

  /**
   * Get the preprocessed information containing binarized image, trimmed image and outline data
   * @returns {PreProcessInfo | null} The preprocessed information or null if not initialized
   */
  get preprocessedInfo() {
    return this.preprocessInfo;
  }

  /**
   * Get process result from cache using cache key or last processed cache key
   * @param targetSizeMm - Optional target size to generate cache key
   * @param offsetDistanceMm - Optional offset distance to generate cache key
   * @param smoothOffsetAdder - Optional smooth offset adder to generate cache key
   * @returns CutlineProcessResult or null if not found
   */
  private getProcessResult(
    targetSizeMm?: Size,
    offsetDistanceMm?: number,
    smoothOffsetAdder?: number,
  ): CutlineProcessResult | null {
    let cacheKey: ProcessCacheKey | null;

    if (targetSizeMm && offsetDistanceMm !== undefined) {
      cacheKey = StickerizerHelper.generateProcessCacheKey(targetSizeMm, offsetDistanceMm, smoothOffsetAdder);
    } else {
      cacheKey = this.lastProcessCacheKey;
    }

    if (!cacheKey) return null;

    return this.processResultMap.get(cacheKey) || null;
  }
}
